import { useState, useCallback } from 'react';
import { fraudProtectionApi } from '../api/fraudProtection';
import type { FraudProtectionSettings, FraudFlaggedOrder } from '../api/fraudProtection';

export function useFraudProtection() {
  const [settings, setSettings] = useState<FraudProtectionSettings | null>(null);
  const [flaggedOrders, setFlaggedOrders] = useState<FraudFlaggedOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fraudProtectionApi.settings();
      setSettings(res.data.data);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load fraud protection settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFlaggedOrders = useCallback(async (p = 1) => {
    try {
      const res = await fraudProtectionApi.flaggedOrders(p);
      setFlaggedOrders(res.data.data);
      setTotal(res.data.meta.total);
      setLastPage(res.data.meta.last_page);
      setPage(p);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load flagged orders.');
    }
  }, []);

  const saveSettings = useCallback(async (payload: FraudProtectionSettings) => {
    setSaving(true);
    setError(null);
    try {
      const res = await fraudProtectionApi.updateSettings(payload);
      setSettings(res.data.data);
      return res.data.data;
    } catch (err: any) {
      setError(err?.message ?? 'Failed to save fraud protection settings.');
      return null;
    } finally {
      setSaving(false);
    }
  }, []);

  return {
    settings, flaggedOrders, loading, saving, error, total, page, lastPage,
    fetchSettings, fetchFlaggedOrders, saveSettings, setError,
  };
}
